import { Link } from 'react-router-dom';
import type { PairSummaryDto } from '@rhcst/shared';
import { tokenLabel, formatEth, formatFeeTier, relativeAge } from '../lib/format.js';
import { RiskBadge } from './RiskBadge.js';
import { EnrichmentBadge } from './EnrichmentBadge.js';
import { OriginBadge } from './OriginBadge.js';

/**
 * One row of the pair feed.
 *
 * A dense list row, not a table cell — the whole row is one link, so the target
 * is the size of the row rather than the size of a token symbol.
 *
 * The information order is what the operator decides on, coarsest first: what it
 * is, then the facts that qualify it (liquidity, age, fee tier), then the risk
 * verdict pushed right where the eye lands last and the decision is made.
 *
 * v2 adds 4 status dots (lifecycle / approval-eligible / reputation /
 * provenance risk) before the risk badge — see `dotTone`'s own comments for
 * the tone mapping, and `Pairs.tsx` for the legend explaining them. Exactly
 * 4; informational only. The row itself stays the click target — clicking a
 * dot does the same thing as clicking anywhere else in the row.
 */

type DotTone = 'good' | 'caution' | 'bad' | 'unscored';

/**
 * Lifecycle status has real "good" states (`candidate`/`approval_eligible`
 * are the cascade's own positive outcomes), so it is the one dot that can
 * actually read green.
 */
function lifecycleTone(status: string | null): DotTone {
  if (status === null || status === 'archived') return 'unscored';
  if (status === 'candidate' || status === 'approval_eligible') return 'good';
  if (status === 'suspected_rug' || status === 'rugged' || status === 'blocked' || status === 'dead_liquidity') {
    return 'bad';
  }
  return 'caution'; // watch, new, settling, stale
}

/** `false` reads as "not yet qualified", not as a penalty. */
function approvalTone(eligible: boolean): DotTone {
  return eligible ? 'good' : 'unscored';
}

/**
 * Reputation and provenance have no "verified clean" value in the data --
 * only "no finding" or a graded bad one -- so unlike lifecycle, these two
 * dots never read green. Showing green here would fabricate a positive
 * finding the backend never made; gray for "no finding" is the honest
 * reading, matching the same absence-is-not-evidence posture the scoring
 * engine itself already applies.
 */
function reputationTone(riskLevel: 'none' | 'watch' | 'severe' | 'critical' | undefined): DotTone {
  if (riskLevel === undefined || riskLevel === 'none') return 'unscored';
  if (riskLevel === 'watch') return 'caution';
  return 'bad'; // severe, critical
}

function provenanceTone(riskLevel: 'unknown' | 'medium' | 'high' | 'critical'): DotTone {
  if (riskLevel === 'unknown') return 'unscored';
  if (riskLevel === 'medium') return 'caution';
  return 'bad'; // high, critical
}

export function StatusDots({ pair }: { pair: PairSummaryDto }) {
  const dots: Array<{ label: string; tone: DotTone }> = [
    { label: `Lifecycle: ${pair.lifecycle_status ?? 'not classified'}`, tone: lifecycleTone(pair.lifecycle_status) },
    {
      label: pair.approval_eligible ? 'Approval eligible' : 'Not approval eligible',
      tone: approvalTone(pair.approval_eligible),
    },
    {
      label: `Reputation: ${pair.actor_reputation?.risk_level ?? 'no finding'}`,
      tone: reputationTone(pair.actor_reputation?.risk_level),
    },
    { label: `Provenance risk: ${pair.provenance_risk_level}`, tone: provenanceTone(pair.provenance_risk_level) },
  ];

  return (
    <span className="status-dots">
      {dots.map((dot) => (
        <span key={dot.label} className="status-dot" data-tone={dot.tone} role="img" aria-label={dot.label} title={dot.label} />
      ))}
    </span>
  );
}

export function PairSymbols({ pair }: { pair: PairSummaryDto }) {
  // Ordered so the quote asset reads second, matching how a trader says it:
  // "MOON / WETH".
  const quoteIsToken0 = pair.quote_token_address === pair.token0.address;
  const base = quoteIsToken0 ? pair.token1 : pair.token0;
  const quote = quoteIsToken0 ? pair.token0 : pair.token1;

  const baseLabel = tokenLabel(base);
  const quoteLabel = tokenLabel(quote);

  return (
    <>
      {/* `tokenLabel` reports whether the text is a real symbol or a fallback.
          That distinction matters: "this token is called WETH" and "this token's
          metadata did not resolve" must not look the same. */}
      <span className={baseLabel.isSymbol ? undefined : 'token-unknown'}>{baseLabel.text}</span>
      <span className="muted"> / </span>
      <span className={quoteLabel.isSymbol ? undefined : 'token-unknown'}>{quoteLabel.text}</span>
    </>
  );
}

/** The non-quote side, same derivation `PairSymbols` uses. Exported so
 *  callers that need the base token alone (e.g. a contract-safety chip)
 *  don't duplicate the quote/base logic. */
export function baseTokenOf(pair: PairSummaryDto) {
  const quoteIsToken0 = pair.quote_token_address === pair.token0.address;
  return quoteIsToken0 ? pair.token1 : pair.token0;
}

export interface PairRowProps {
  pair: PairSummaryDto;
  onToggleWatch: (pair: PairSummaryDto) => void;
  busy: boolean;
  /** True for rows that arrived since the operator opened the page. */
  fresh?: boolean;
}

export function PairRow({ pair, onToggleWatch, busy, fresh = false }: PairRowProps) {
  const feeTier = formatFeeTier(pair.fee_tier);
  const blocklisted = pair.token0.is_blocklisted || pair.token1.is_blocklisted;

  return (
    <div className="list-row" data-fresh={fresh}>
      <Link to={`/pairs/${pair.id}`} className="row-main">
        <div className="row-pair">
          <PairSymbols pair={pair} />
        </div>
        <div className="row-meta">
          <span className="row-fact">{formatEth(pair.liquidity_eth)} ETH</span>
          <span className="row-fact">{relativeAge(pair.first_seen_at)} ago</span>
          {feeTier && <span className="row-fact">{feeTier}</span>}
          <EnrichmentBadge enrichment={pair.enrichment} />
          <OriginBadge origin={pair.origin} />
          <StatusDots pair={pair} />
          {blocklisted && (
            <span className="chip" data-tone="alarm">
              Blocklisted
            </span>
          )}
          {/* A pulled LP is the strongest negative signal this system produces,
              so it is rendered in the hazard treatment rather than as another
              grey status chip. */}
          {pair.status === 'rugged' && (
            <span className="veto-block" title="Liquidity was pulled from this pool. The system marked it terminally rugged and stopped re-checking it.">
              Rugged
            </span>
          )}
        </div>
      </Link>

      <RiskBadge risk={pair.risk} />

      <button
        className="btn btn-mini"
        type="button"
        onClick={() => onToggleWatch(pair)}
        disabled={busy}
        aria-pressed={pair.is_watched}
      >
        {pair.is_watched ? 'Watching' : 'Watch'}
      </button>
    </div>
  );
}
